import JSON5 from "json5";
import { z } from "zod";

import { getNormalEnum } from "./pseudoEnums.js";
import { NumberOrNaN } from "./zod/utils.js";

// !!! IMPORTANT !!! This file to be kept in sync with @hex/python-kernel-startup/python_kernel_startup/formatters.py

// Dynamic values allow for the ability to use the value of a given variable
// defined in the kernel elsewhere (e.g. the frontend).
// Since reading the underlying value requires code execution, we store
// the resolved (or "filled") value in a cell output of type FilledDynamicValue.
// To resolve all filled dynamic values for a cell, one simply needs
// to get all outputs and filter to the filled dynamic values using MIMETYPES
export const DynamicValue = z.object({
  variableName: z.string().or(z.null()),
});
export type DynamicValue = z.infer<typeof DynamicValue>;

/**
 * `dfName` must be *present*, though it may be `undefined`. That presence is
 * what tells a dataframe value from a plain {@link DynamicValue}, which has
 * only `variableName`: the runtypes schema this replaced (`String.Or(Undefined)`
 * on a non-optional key) rejected a missing key, while zod's
 * `z.string().or(z.undefined())` alone accepts one -- which let every plain
 * dynamic value pass as a dataframe value. zod keeps a key in its output when
 * the input had one, even as `undefined`, so the check reads presence there.
 */
export const DynamicDataFrameValue = z
  .object({
    variableName: z.string().or(z.null()),
    dfName: z.string().or(z.undefined()),
    columnName: z.string().optional(),
    displayColumnName: z.string().optional(),
  })
  .refine((value) => "dfName" in value, {
    message: "dfName must be present",
    path: ["dfName"],
  });
export type DynamicDataFrameValue = z.infer<typeof DynamicDataFrameValue>;

// Dataframe first: `z.union` returns the first member that parses, and a
// `DynamicValue` parse would strip `dfName` and the column names from a
// dataframe value. (The runtypes union returned its input untouched.)
export const DynamicValueTypes = z.union([DynamicDataFrameValue, DynamicValue]);

// MIME type is FILLED_DYNAMIC_VALUE_MIMETYPE
export const FilledDynamicValue = z.object({
  variableName: z.string(),
  value: z.unknown(),
  truncated: z.boolean().or(z.undefined()),
});
export type FilledDynamicValue = z.infer<typeof FilledDynamicValue>;

// Most data types are straight forward to serialize for dynamic values,
// however, tables require an agreed upon format, so below is a set of types
// for how we serialize pandas dataframes

// @WARN: must be kept in sync with zod types in src/agents/ask/types.ts
export const FilledDynamicValueTableColumnTypeLiteral = z.enum([
  "STRING",
  "NUMBER",
  "DATETIME",
  "DATETIMETZ",
  "DATE",
  "TIME",
  "UNKNOWN",
  "BOOLEAN",
]);
export type FilledDynamicValueTableColumnType = z.infer<
  typeof FilledDynamicValueTableColumnTypeLiteral
>;
export const FilledDynamicValueTableColumnType = getNormalEnum(
  FilledDynamicValueTableColumnTypeLiteral,
);

export const FilledDynamicValueTableHeader = z.object({
  id: z.string(), // a stable uuid across runs
  columnName: z.union([z.string(), z.number()]),
  type: FilledDynamicValueTableColumnTypeLiteral,
});
export type FilledDynamicValueTableHeader = z.infer<
  typeof FilledDynamicValueTableHeader
>;

export const FilledDynamicValueTableHeaders = z.array(
  FilledDynamicValueTableHeader,
);
export type FilledDynamicValueTableHeaders = z.infer<
  typeof FilledDynamicValueTableHeaders
>;

// header id -> contents
export const FilledDynamicValueTableContents = z.record(
  z.string(),
  z.array(
    z.union([z.string(), z.null(), z.undefined(), NumberOrNaN, z.boolean()]),
  ),
);
export type FilledDynamicValueTableContents = z.infer<
  typeof FilledDynamicValueTableContents
>;

export const FilledDynamicValueTable = z.object({
  // if `contents_column_names` argument is provided when making
  // the kernel request, we only get back contents data for the
  // column names listed in the argument, the motivation being
  // not fetching unnecessary data and reducing data sent over the wire
  contents: FilledDynamicValueTableContents,
  headers: FilledDynamicValueTableHeaders,
  rowCount: z.number(),
  totalRowCount: z.number().optional(),
  preview: z.boolean().optional(),
});
export type FilledDynamicValueTable = z.infer<typeof FilledDynamicValueTable>;

export interface ParseFilledDynamicValueOptions {
  /**
   * Called when parsing fails. Use for logging (e.g. console.error on client).
   */
  onError?: (error: unknown) => void;
  /**
   * Called when falling back to JSON5 parsing (e.g. due to NaN values).
   * Useful for monitoring slow parses on large datasets.
   */
  onJson5Fallback?: (contentsLength: number) => void;
}

/**
 * Parses a double-JSON-encoded FilledDynamicValue from output contents.
 * Returns null if parsing or validation fails.
 */
export function parseFilledDynamicValue(
  contents: string | null | undefined,
  options?: ParseFilledDynamicValueOptions,
): FilledDynamicValue | null {
  if (contents == null) {
    return null;
  }

  try {
    const innerJson = JSON.parse(contents);

    let outerData: unknown;
    try {
      outerData = JSON.parse(innerJson);
    } catch (e: unknown) {
      if (e instanceof SyntaxError) {
        // We use JSON5 to properly handle NaN (which is allowed in python json.dumps),
        // but which is not technically valid JSON.
        // Unfortunately, JSON5 is extremely slow for some datasets, so we first try to parse
        // using the much faster native JSON.parse.
        options?.onJson5Fallback?.(innerJson?.length ?? 0);
        outerData = JSON5.parse(innerJson);
      } else {
        throw e;
      }
    }

    const result = FilledDynamicValue.safeParse(outerData);
    if (result.success) {
      return result.data;
    }
    return null;
  } catch (error) {
    options?.onError?.(error);
    return null;
  }
}

/**
 * Parses multiple output contents into a map of variable name -> FilledDynamicValue.
 */
export function parseFilledDynamicValues(
  outputContents: { contents: string | null | undefined }[],
  options?: ParseFilledDynamicValueOptions,
): Record<string, FilledDynamicValue> {
  const result: Record<string, FilledDynamicValue> = {};

  for (const output of outputContents) {
    const parsed = parseFilledDynamicValue(output.contents, options);
    if (parsed) {
      result[parsed.variableName] = parsed;
    }
  }

  return result;
}
